import React, { useEffect, useState } from 'react'
import { IPromise } from 'angular'
import { reactular } from '@vendhq/reactular'
import { Box } from '@lightspeed/design-system-react'
import { ApiCustomer } from '@vend/types'
import {
  CUSTOMER_FIELD,
  CustomerInfo,
  PartialCustomerGroup,
} from '../common/types'
import { contactUpdated } from '../common/customer-contact-utils'
import { useVdFeatureManager } from '../../../../ng-compat/hooks'
import {
  ALL_CUSTOMERS_GROUP_ID,
  detectCustomerFieldFromQuery,
} from '../../../../functions/customer'
import { customerGroupsStore } from '../../../../factories/database/customer-groups-store'
import { $q } from '../../../../ng-compat/imports'
import {
  selectCurrentSale,
  selectSaleNote,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../../../state/hooks'
import {
  ServiceAgenda,
  ServiceItemData,
} from '../../../../models/sale.constants'
import { useCustomerGroups } from '../common/hooks/useCustomerGroups'
import {
  StepSelectCustomer,
  StepServiceItem,
  StepWorkDescription,
  StepSchedule,
} from './Steps'
import { CUSTOMER_STEP_VIEW, STEPS } from './Steps/types'
import { ButtonValue } from './Steps/StepServiceItem/ItemSegmentedControl'

interface Props {
  close: (val?: IPromise<any>) => void
  startWithStep?: STEPS
  isEditing?: boolean
}

export const ServiceModal: React.FC<Props> = ({
  close,
  startWithStep = STEPS.SELECT_CUSTOMER, // just in case this is being used directly from react in the future
  isEditing = false, // flag used to track if the modal is open using the Edit button/flow
}) => {
  const [currentStep, setCurrentStep] = useState<number>(startWithStep)
  const [selectedCustomer, setSelectedCustomer] = useState<ApiCustomer | null>(
    null
  )

  const currentSale = useAppSelector(selectCurrentSale)
  const [newCustomer, setNewCustomer] = useState<CustomerInfo | null>(null)
  const [group, setGroup] = useState<PartialCustomerGroup | undefined>(
    undefined
  )
  const [originalCustomer, setOriginalCustomer] = useState<ApiCustomer | null>(
    null
  )

  const [serviceAgenda, setServiceAgenda] = useState<ServiceAgenda | undefined>(
    currentSale.serviceFields.serviceAgenda
  )

  const saleNote = useAppSelector(selectSaleNote)
  const [serviceItem, setServiceItem] = useState<ServiceItemData | undefined>(
    currentSale.serviceFields.serviceItem
  )
  const [note, setNote] = useState<string>(saleNote)

  const [contactChanged, setContactChanged] = useState<boolean>(false)
  const [segButton, setSegButton] = useState<ButtonValue>(ButtonValue.ADD_ITEM)

  const vdFeatureManager = useVdFeatureManager()
  const shouldShowGroup = vdFeatureManager.get('customer_groups')

  const allCustomerGroups = useCustomerGroups()

  /**
   * Set the default group
   */
  const setDefaultGroup = () => {
    if (allCustomerGroups) {
      const defaultGroup = allCustomerGroups.find(
        item => item.groupId === ALL_CUSTOMERS_GROUP_ID
      )
      defaultGroup &&
        setGroup({
          id: defaultGroup.id,
          name: defaultGroup.name,
        })
    }
  }

  if (
    shouldShowGroup &&
    selectedCustomer &&
    selectedCustomer.customer_group_id &&
    !group
  ) {
    customerGroupsStore
      .get(selectedCustomer.customer_group_id)
      .then(groupEntity => {
        if (groupEntity) {
          setGroup({
            id: groupEntity.id,
            name: groupEntity.name,
          })
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.log('Error getting customer group', error)
        setDefaultGroup()
      })
  }
  if (newCustomer && !group) {
    setDefaultGroup()
  }

  const closeAngularModal = (shouldCreate?: boolean) => {
    close($q.resolve(shouldCreate))
  }

  const handleStepChange = (step: STEPS) => {
    setCurrentStep(step)
  }

  const [customerStepView, setCustomerStepView] = useState<CUSTOMER_STEP_VIEW>(
    CUSTOMER_STEP_VIEW.SELECT_CUSTOMER
  )

  const handleAddCustomer = (newName?: string) => {
    if (newName) {
      const customer = detectCustomerFieldFromQuery(newName)
      setNewCustomer(prevState => ({
        ...(prevState as CustomerInfo),
        ...customer,
      }))
      setSelectedCustomer(null)
      setOriginalCustomer(null)
      setCustomerStepView(CUSTOMER_STEP_VIEW.ADD_NEW_CUSTOMER)
    }
  }

  const handleSelectCustomer = (customer: ApiCustomer | null) => {
    if (customer) {
      setSelectedCustomer(customer)
      setOriginalCustomer(customer)
      setNewCustomer(null)
    } else {
      setSelectedCustomer(null)
    }
    // Reset the serviceItem when the customer is changed to avoid showing stale data.
    setServiceItem({})
  }

  const handleSelectedContactChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault()
    const { value, name } = e.target
    if (name) {
      if (name === CUSTOMER_FIELD.GROUP) {
        setGroup((value as unknown) as PartialCustomerGroup)
        setSelectedCustomer((prev: $TSFixMe) => {
          return {
            ...prev,
            customer_group_id: ((value as unknown) as PartialCustomerGroup)?.id,
          }
        })
        return
      }
      setSelectedCustomer((prev: $TSFixMe) => {
        return {
          ...prev,
          [name]: value,
        }
      })
    }
  }

  const handleNewContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    const { value, name } = e.target

    if (name) {
      if (name === CUSTOMER_FIELD.GROUP) {
        setGroup((value as unknown) as PartialCustomerGroup)
        setNewCustomer((prev: $TSFixMe) => {
          return {
            ...prev,
            customer_group_id: ((value as unknown) as PartialCustomerGroup)?.id,
          }
        })
        return
      }
      setNewCustomer(prevState => ({
        ...(prevState as CustomerInfo),
        [name]: value,
      }))
    }
  }

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target
    if (value) {
      setNote(value)
    } else {
      setNote('')
    }
  }

  const handleServiceAgendaChange = (agenda?: ServiceAgenda) => {
    setServiceAgenda(agenda)
  }

  const handleServiceItem = (serviceItem: ServiceItemData) => {
    setServiceItem(serviceItem)
  }
  useEffect(() => {
    if (
      currentSale &&
      currentSale.customer &&
      currentSale.customer.customer_code !== 'WALKIN'
    ) {
      setOriginalCustomer(currentSale.customer)
      setSelectedCustomer(currentSale.customer)
    }
  }, [currentSale, setSelectedCustomer])

  useEffect(() => {
    setContactChanged(contactUpdated(selectedCustomer, originalCustomer))
  }, [selectedCustomer, originalCustomer])

  const getStepComponent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepSelectCustomer
            onClose={closeAngularModal}
            onStepChange={handleStepChange}
            onAdd={handleAddCustomer}
            onSelect={handleSelectCustomer}
            onContactChange={event => {
              customerStepView === CUSTOMER_STEP_VIEW.ADD_NEW_CUSTOMER
                ? handleNewContactChange(event)
                : handleSelectedContactChange(event)
            }}
            selectedCustomer={selectedCustomer}
            newCustomer={newCustomer}
            group={group}
            isEditing={isEditing}
            customerStepView={customerStepView}
            setCustomerStepView={setCustomerStepView}
          />
        )
      case 2:
        return (
          <StepServiceItem
            onClose={closeAngularModal}
            onStepChange={handleStepChange}
            newCustomer={newCustomer}
            selectedCustomer={selectedCustomer}
            serviceItem={serviceItem}
            onServiceItemChange={handleServiceItem}
            segButton={segButton}
            onSegButtonSelect={setSegButton}
          />
        )
      case 3:
        return (
          <StepWorkDescription
            onClose={closeAngularModal}
            onStepChange={handleStepChange}
            onNoteChange={handleNoteChange}
            onServiceItemChange={handleServiceItem}
            note={note}
            serviceItem={serviceItem}
          />
        )
      case 4:
        return (
          <StepSchedule
            onClose={closeAngularModal}
            onStepChange={handleStepChange}
            onServiceChange={handleServiceAgendaChange}
            group={group}
            newCustomer={newCustomer}
            contactChanged={contactChanged}
            serviceAgenda={serviceAgenda}
            serviceItem={serviceItem}
            selectedCustomer={selectedCustomer}
            note={note}
            isEditing={isEditing}
          />
        )
    }
  }

  return (
    <Box className="service-modal" data-cy="service-modal">
      {getStepComponent()}
    </Box>
  )
}

ServiceModal.displayName = 'ServiceModal'

export const ServiceModalComponent = reactular(
  ServiceModal,
  ['close', 'startWithStep', 'isEditing'],
  'reactWrapper'
)
